"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { Container, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import { extractProperty } from "utils"
import MainNews from "./MainNews"

const StyledNewsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  grid-row-gap: 56px;
  margin-top: 112px;

  margin-bottom: 80px;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 1fr 1fr;
    grid-row-gap: 64px;
    grid-column-gap: 16.5px;
    margin-top: 88px;
  }
`

const StyledLoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 153px;
    margin: auto;
  }
`

type Props = {
  data: any
}

function MoreNews({ data }: Props) {
  const newsItem = extractProperty(data, "newsItem") || []

  // State to manage the number of visible items
  const [visibleItems, setVisibleItems] = useState(6)

  // Track the index of the first new item to focus on
  const firstNewItemIndex = useRef<number | null>(null)
  const newsItemRefs = useRef<(HTMLDivElement | null)[]>([])

  // Function to handle "Load More" button click
  const handleLoadMore = () => {
    firstNewItemIndex.current = visibleItems
    setVisibleItems((prevVisibleItems) => prevVisibleItems + 6)
  }

  // Focus on the first new item after loading more content
  useEffect(() => {
    if (firstNewItemIndex.current !== null) {
      const indexToFocus = firstNewItemIndex.current
      firstNewItemIndex.current = null

      let attempts = 0
      const maxAttempts = 20
      const retryInterval = 150

      const tryFocus = () => {
        attempts++
        const targetElement = newsItemRefs.current[indexToFocus]

        if (targetElement) {
          const anchorElement = targetElement.querySelector("a")

          if (anchorElement) {
            const rect = anchorElement.getBoundingClientRect()
            const styles = window.getComputedStyle(anchorElement)
            const isVisible = rect.height > 0 && parseFloat(styles.opacity) > 0.5

            if (isVisible) {
              targetElement.scrollIntoView({ behavior: "smooth", block: "start" })
              setTimeout(() => {
                anchorElement.focus({ preventScroll: true })
              }, 500)
              return
            }
          }
        }

        if (attempts < maxAttempts) {
          setTimeout(tryFocus, retryInterval)
        }
      }

      setTimeout(tryFocus, 100)
    }
  }, [visibleItems])

  const { ref: mobileRef, inView: mobileInView } = useInView(useInViewConfig)

  return (
    <>
      <div ref={mobileRef}> </div>
      <Container>
        <StyledNewsContainer>
          {newsItem.slice(0, visibleItems).map((news: any, index: number) => (
            <TransitionUp
              key={index.toString()}
              $inView={mobileInView}
              $delay={transitionDelayTime * 0.01 * index}
              style={{ height: "100%" }}
            >
              <div
                ref={(el) => {
                  newsItemRefs.current[index] = el
                }}
                style={{ display: "contents" }}
              >
                <MainNews data={news} isSmall />
              </div>
            </TransitionUp>
          ))}
        </StyledNewsContainer>
        <TransitionUp $inView={mobileInView} $delay={transitionDelayTime * 10}>
          <StyledLoadMoreContainer>
            {visibleItems < newsItem.length && (
              <Button
                style={{ textTransform: "none", width: "100%" }}
                variant="contained"
                size="small"
                onClick={handleLoadMore}
              >
                Load More
              </Button>
            )}
          </StyledLoadMoreContainer>
        </TransitionUp>
      </Container>
    </>
  )
}

export default MoreNews
